import { getEnv } from '@/lib/env';

import type { ChannelConnectionState } from '@/hooks/useChannelSession';

// ── WebRTC connection (extracted from OperatorCallDialog) ─────────

export interface WebRtcHandle {
  stop: () => void;
  toggleMute: () => boolean;
}

/** Real call lifecycle as reported by the telephony provider (not the operator's WebRTC link). */
export type CallLifecycleStatus = 'ringing' | 'answered' | 'declined' | 'ended';

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

export function connectWebRtc(
  wsPath: string,
  wsToken: string,
  callSid: string,
  callId: string,
  onState: (state: ChannelConnectionState) => void,
  onError: (msg: string) => void,
  onEnded: () => void,
  onCallStatus?: (status: CallLifecycleStatus) => void,
): WebRtcHandle {
  let peer: RTCPeerConnection | null = null;
  let localStream: MediaStream | null = null;
  let remoteAudio: HTMLAudioElement | null = null;
  let ws: WebSocket | null = null;
  let isStopping = false;
  let iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS;
  let iceServersResolver: (() => void) | null = null;
  let negotiationAttempts = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const MAX_NEGOTIATION_ATTEMPTS = 3;

  const cleanup = () => {
    if (peer) {
      peer.onicecandidate = null;
      peer.onconnectionstatechange = null;
      peer.ontrack = null;
      peer.close();
      peer = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((t) => t.stop());
      localStream = null;
    }
    if (remoteAudio) {
      remoteAudio.srcObject = null;
      remoteAudio = null;
    }
  };

  const closeSocket = () => {
    const socket = ws;
    ws = null;
    if (!socket) return;
    if (socket.readyState === WebSocket.OPEN) {
      socket.close(1000, 'client_close');
      return;
    }
    socket.close();
  };

  // Tear down the failed peer and try the full offer/answer again. The backend
  // replaces its side of the bridge on a new offer, so renegotiation is safe.
  const scheduleRenegotiate = (reason: string) => {
    if (isStopping || retryTimer) return;
    console.warn(`[WebRTC] connection lost (${reason}), attempt ${negotiationAttempts}`);

    if (negotiationAttempts >= MAX_NEGOTIATION_ATTEMPTS) {
      onState('error');
      onError('WebRTC connection failed.');
      return;
    }

    cleanup();
    onState('connecting');
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void startPeerNegotiation();
    }, 1500);
  };

  const startPeerNegotiation = async () => {
    if (ws?.readyState !== WebSocket.OPEN) return;
    if (peer || isStopping) return;

    if (typeof RTCPeerConnection === 'undefined') {
      onState('error');
      onError('WebRTC is not supported in this browser.');
      return;
    }

    negotiationAttempts += 1;

    try {
      // Wait briefly for TURN servers from the signaling channel
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(() => {
          iceServersResolver = null;
          resolve();
        }, 2500);
        iceServersResolver = () => {
          clearTimeout(timeout);
          iceServersResolver = null;
          resolve();
        };
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      peer = new RTCPeerConnection({
        iceServers,
        bundlePolicy: 'max-bundle',
      });

      const remoteStream = new MediaStream();
      remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      remoteAudio.srcObject = remoteStream;
      localStream = stream;

      stream.getTracks().forEach((track) => {
        peer!.addTrack(track, stream);
      });

      peer.onicecandidate = (event) => {
        if (ws?.readyState !== WebSocket.OPEN) return;
        ws.send(
          JSON.stringify({
            type: 'webrtc_ice_candidate',
            candidate: event.candidate ? event.candidate.toJSON() : null,
          }),
        );
      };

      peer.onconnectionstatechange = () => {
        if (!peer) return;
        const state = peer.connectionState;
        console.log('[WebRTC] peer connection state:', state);
        if (state === 'connected') {
          negotiationAttempts = 0;
          onState('connected');
        } else if (state === 'failed') {
          scheduleRenegotiate('peer_failed');
        } else if (state === 'closed' && !isStopping) {
          scheduleRenegotiate('peer_closed');
        }
        // 'disconnected' is often transient — let ICE try to recover.
      };

      peer.oniceconnectionstatechange = () => {
        console.log('[WebRTC] ICE connection state:', peer?.iceConnectionState);
      };

      peer.ontrack = (event) => {
        if (event.track.kind !== 'audio') return;
        console.log('[WebRTC] remote audio track received');
        remoteStream.addTrack(event.track);
        void remoteAudio
          ?.play()
          .catch((err) => console.warn('[WebRTC] remote audio playback blocked:', err));
      };

      const offer = await peer.createOffer({ offerToReceiveAudio: true });
      await peer.setLocalDescription(offer);

      ws.send(
        JSON.stringify({
          type: 'webrtc_offer',
          sdp: offer.sdp ?? '',
        }),
      );
    } catch (err) {
      cleanup();
      // Mic permission problems won't fix themselves — fail immediately.
      const fatal =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'NotFoundError');
      if (fatal) {
        onState('error');
        onError(err instanceof Error ? err.message : 'Failed to start WebRTC negotiation.');
      } else {
        scheduleRenegotiate('negotiation_error');
      }
    }
  };

  // Build WebSocket URL
  const apiBase = getEnv('VITE_API_URL');
  const apiOrigin = new URL(apiBase);
  const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
  const effectiveToken =
    wsToken === 'USE_ACCESS_TOKEN' ? (localStorage.getItem('accessToken') ?? '') : wsToken;
  const wsUrl = `${wsProtocol}//${apiOrigin.host}${wsPath}/${encodeURIComponent(effectiveToken)}`;

  ws = new WebSocket(wsUrl);
  onState('connecting');

  ws.onmessage = (event: MessageEvent<string>) => {
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(event.data) as Record<string, unknown>;
    } catch {
      return;
    }

    console.log('[WebRTC] WS message:', message.type, message);

    switch (message.type) {
      case 'ready':
        if (wsToken !== 'USE_ACCESS_TOKEN') void startPeerNegotiation();
        return;
      case 'webrtc_ice_servers':
        iceServers = (message.servers as RTCIceServer[])?.length
          ? (message.servers as RTCIceServer[])
          : DEFAULT_ICE_SERVERS;
        iceServersResolver?.();
        return;
      case 'webrtc_answer':
        if (peer && message.sdp) {
          void peer.setRemoteDescription({
            type: 'answer',
            sdp: message.sdp as string,
          });
        }
        return;
      case 'webrtc_ice_candidate':
        if (peer && message.candidate) {
          void peer.addIceCandidate(message.candidate as RTCIceCandidateInit);
        }
        return;
      case 'webrtc_state': {
        const enabled = message.enabled as boolean;
        const status = message.status as string;
        if (!enabled || status === 'unsupported') {
          onState('error');
          onError((message.reason as string) || 'WebRTC backend is unavailable.');
          isStopping = true;
          closeSocket();
        } else if (status === 'error') {
          // Backend bridge peer dropped — renegotiate instead of giving up.
          scheduleRenegotiate('server_bridge_error');
        } else if (status === 'connected') {
          onState('connected');
        }
        return;
      }
      case 'call_status':
        onCallStatus?.(message.status as CallLifecycleStatus);
        return;
      case 'ended':
        onState('ended');
        cleanup();
        closeSocket();
        onEnded();
        return;
      case 'not_live':
        subscribeRetryTimer = setTimeout(trySubscribe, 2000);
        return;
      case 'subscribed':
        if (subscribeRetryTimer) clearTimeout(subscribeRetryTimer);
        return;
      case 'takeover_state':
        if (message.active) void startPeerNegotiation();
        return;
      case 'error':
        onState('error');
        onError(message.error as string);
        return;
      default:
        return;
    }
  };

  let subscribeRetryTimer: ReturnType<typeof setTimeout> | null = null;
  const trySubscribe = () => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'subscribe', callId }));
    }
  };

  ws.onopen = () => {
    if (wsToken === 'USE_ACCESS_TOKEN' && callSid) {
      trySubscribe();
    }
  };

  ws.onerror = () => {
    onState('error');
    onError('WebSocket connection error.');
    isStopping = true;
    closeSocket();
  };

  ws.onclose = () => {
    iceServersResolver?.();
    if (subscribeRetryTimer) clearTimeout(subscribeRetryTimer);
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (isStopping) return;
    cleanup();
    onEnded();
  };

  return {
    stop: () => {
      isStopping = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'unsubscribe' }));
      }
      cleanup();
      closeSocket();
    },
    toggleMute: () => {
      if (!localStream) return false;
      const track = localStream.getAudioTracks()[0];
      if (!track) return false;
      track.enabled = !track.enabled;
      return !track.enabled; // true = muted
    },
  };
}
